import * as React from 'react';
import { Skeleton } from '@chess/api-skeleton';
import { Pie, PieLegend, TextSkeleton, type CardProps, type FilterLink, type PieSlice } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import type { ChangeBand, StageChange } from '@chess/api-model';
import { CHANGE_BANDS, type OpeningResultsResult } from './OpeningResultsAggregator.ts';

// "Opening results": the period's scanned games by the score they came out
// of the opening with, as a pie of the five change bands in the colours the
// "How each stage goes" bars use. Every slice and legend entry opens exactly
// those games in the library (`stage-change`). The Openings section's first
// card (2026-10-08), the mirror of Middlegame results and Ending results.
// Drawn from props only; its frame (title, aside, note) is the section's
// (`registerCard`, @chess/api-dashboard).

export type OpeningResultsCardProps = CardProps<{ result: OpeningResultsResult }, {
  /** Opens the library filtered to a slice's games. */
  link?: FilterLink;
}>;

export const TITLE = 'Opening results';
const ASIDE = 'score out of the opening';
const SIZE = 96;
export const BAND_LABEL: Record<ChangeBand, string> = { gained: 'Gained score', 'slightly-gained': 'Gained a little', held: 'Held', 'slightly-lost': 'Lost a little', lost: 'Lost score' };
export const BAND_COLOUR: Record<ChangeBand, string> = {
  gained: 'var(--viz-win)',
  'slightly-gained': 'color-mix(in srgb, var(--viz-win) 45%, var(--viz-draw))',
  held: 'var(--viz-draw)',
  'slightly-lost': 'color-mix(in srgb, var(--viz-loss) 45%, var(--viz-draw))',
  lost: 'var(--viz-loss)',
};
const BAND_TITLE: Record<ChangeBand, string> = {
  gained: 'out of the opening at +2.00 or better',
  'slightly-gained': 'out of the opening between +0.50 and +2.00',
  held: 'out of the opening within half a pawn of level',
  'slightly-lost': 'out of the opening between −0.50 and −2.00',
  lost: 'out of the opening at −2.00 or worse',
};
const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;

function slicesOf(r: OpeningResultsResult): PieSlice[] {
  return CHANGE_BANDS.map((b) => ({
    key: b,
    label: BAND_LABEL[b],
    count: r.by[b],
    colour: BAND_COLOUR[b],
    title: `${BAND_TITLE[b]} · opens those games`,
    filter: { stageChange: `opening-${b}` as StageChange },
  }));
}

const LoadingBody: React.FC = () => (
  <div className="donut-wrap">
    <Skeleton loading variant="circular" width={`${SIZE}px`} height={`${SIZE}px`} label="Loading the opening results" />
    <ul className="legend">
      {CHANGE_BANDS.map((b) => (
        <li key={b}>
          <span className="legend-swatch" style={{ background: BAND_COLOUR[b] }} />
          {BAND_LABEL[b]}
          <span className="legend-value">
            <Skeleton loading inline variant="text" shape="12 · 34%" />
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export const OpeningResultsCard: React.FC<OpeningResultsCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <LoadingBody />
        <CardNote>
          <TextSkeleton sample="135 games scored · a slice opens them" />
        </CardNote>
      </>
    );
  const { result, link } = props;
  const slices = slicesOf(result);
  const total = slices.reduce((n, s) => n + s.count, 0);
  if (!total)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>Scan games to score their openings.</CardEmpty>
      </>
    );
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <div className="donut-wrap">
        <Pie slices={slices} size={SIZE} link={link} />
        <PieLegend slices={slices} link={link} />
      </div>
      <CardNote>{`${games(total)} scored · a slice opens them in the library`}</CardNote>
    </>
  );
};
